import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FlagIcon from "@mui/icons-material/Flag";

const QuestionProgress = ({
  currentPage,
  totalPages,
  totalQuestions,
  answeredCount,
}) => {
  const theme = useTheme();

  // Calculate progress percentage
  const progressPercentage = (currentPage / (totalPages - 1)) * 100;
  const completionPercentage = (answeredCount / totalQuestions) * 100;

  // Calculate which milestone the user has reached
  const milestones = [25, 50, 75, 100];
  const currentMilestone = milestones.findIndex(
    (m) => completionPercentage < m
  );
  const milestoneReached =
    currentMilestone > 0 ? milestones[currentMilestone - 1] : 0;

  // Get encouraging message based on progress
  const getMessage = () => {
    if (completionPercentage < 25) return "Great start! Keep going!";
    if (completionPercentage < 50) return "You're making good progress!";
    if (completionPercentage < 75) return "More than halfway there!";
    if (completionPercentage < 100) return "Almost done! Final push!";
    return "Completed! Well done!";
  };

  return (
    <Box sx={{ mb: 4, width: "100%", position: "relative" }}>
      {/* Top info row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Question {currentPage * 5 + 1}-
          {Math.min((currentPage + 1) * 5, totalQuestions)} of {totalQuestions}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            color: completionPercentage === 100 ? "#12a05c" : "#DF7468",
          }}
        >
          {Math.round(completionPercentage)}% Complete
        </Typography>
      </Box>

      {/* Main progress bar */}
      <Box
        sx={{
          height: 12,
          bgcolor: "#f0f0f0",
          borderRadius: 20,
          position: "relative",
          overflow: "hidden",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${progressPercentage}%`,
            backgroundImage:
              "linear-gradient(90deg, #F590AB 0%, #F2A442 60%, #DF7468 100%)",
            borderRadius: 20,
            transition: "width 0.5s ease-in-out",
          }}
        />
      </Box>

      {/* Milestone markers */}
      <Box sx={{ position: "relative", mt: 0.5, height: 20 }}>
        {milestones.map((milestone, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              left: `${milestone}%`,
              transform: "translateX(-50%)",
              width: 30,
              height: 30,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: completionPercentage >= milestone ? "#12a05c" : "#aaa",
              zIndex: 2,
              transition: "color 0.3s ease",
            }}
          >
            {milestone === 100 ? (
              <FlagIcon fontSize="small" color="inherit" />
            ) : (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {milestone}%
              </span>
            )}
          </Box>
        ))}
      </Box>

      {/* Encouraging message */}
      <Box
        sx={{
          mt: 1,
          textAlign: "center",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: "14px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            fontStyle: "italic",
            color: "#333",
            opacity: 0.9,
          }}
        >
          {getMessage()}
        </Typography>
      </Box>

      {/* Trophy animation when reaching 100% */}
      {completionPercentage === 100 && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: -5,
            animation: "bounce 1s infinite alternate",
            "@keyframes bounce": {
              "0%": { transform: "translateY(0)" },
              "100%": { transform: "translateY(-10px)" },
            },
          }}
        >
          <EmojiEventsIcon sx={{ color: "#FDE470", fontSize: 32 }} />
        </Box>
      )}
    </Box>
  );
};

export default QuestionProgress;
